import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, RotateCcw, Save, Sliders, History } from 'lucide-react';
import { DEFAULT_SCORING_WEIGHTS, useFleetStore } from '@/stores/fleet';
import type { ScoringWeights } from '@/services/api';

const COMPONENT_ORDER: Array<keyof ScoringWeights> = [
  'coverage',
  'density',
  'diversity',
  'leverage',
  'freshness',
];

const COMPONENT_COLORS: Record<keyof ScoringWeights, string> = {
  coverage: 'accent-sky-500',
  density: 'accent-violet-500',
  diversity: 'accent-emerald-500',
  leverage: 'accent-amber-500',
  freshness: 'accent-rose-500',
};

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function normalizeWeights(w: ScoringWeights): ScoringWeights {
  const total = COMPONENT_ORDER.reduce((sum, k) => sum + (w[k] ?? 0), 0);
  if (total <= 0) return { ...DEFAULT_SCORING_WEIGHTS };
  return COMPONENT_ORDER.reduce((acc, k) => {
    acc[k] = Number(((w[k] ?? 0) / total).toFixed(4));
    return acc;
  }, {} as ScoringWeights);
}

interface ScoringWeightEditorProps {
  clientId: string;
}

export function ScoringWeightEditor({ clientId }: ScoringWeightEditorProps) {
  const {
    fleetConfig,
    weightHistory,
    fleetConfigLoading,
    saveScoringWeights,
    resetScoringWeights,
    fetchWeightHistory,
  } = useFleetStore();

  const effectiveWeights: ScoringWeights = useMemo(() => {
    const overrides = fleetConfig?.scoring_weight_overrides;
    if (overrides && Object.keys(overrides).length > 0) {
      return {
        coverage: overrides.coverage ?? DEFAULT_SCORING_WEIGHTS.coverage,
        density: overrides.density ?? DEFAULT_SCORING_WEIGHTS.density,
        diversity: overrides.diversity ?? DEFAULT_SCORING_WEIGHTS.diversity,
        leverage: overrides.leverage ?? DEFAULT_SCORING_WEIGHTS.leverage,
        freshness: overrides.freshness ?? DEFAULT_SCORING_WEIGHTS.freshness,
      };
    }
    return { ...DEFAULT_SCORING_WEIGHTS };
  }, [fleetConfig]);

  const [local, setLocal] = useState<ScoringWeights>(effectiveWeights);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dirty) setLocal(effectiveWeights);
  }, [effectiveWeights, dirty]);

  useEffect(() => {
    if (clientId) fetchWeightHistory(clientId);
  }, [clientId, fetchWeightHistory]);

  const total = COMPONENT_ORDER.reduce((sum, k) => sum + local[k], 0);
  const normalized = useMemo(() => normalizeWeights(local), [local]);

  const handleChange = (key: keyof ScoringWeights, value: number) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      await saveScoringWeights(clientId, normalized);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      await resetScoringWeights(clientId);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  if (!clientId) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sliders size={16} className="text-slate-400" />
          <h2 className="font-semibold text-navy-900 text-sm">Scoring Weights</h2>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          Select a client to edit NVI scoring weights.
        </p>
      </div>
    );
  }

  const sumOffBy = Math.abs(total - 1);
  const usingOverrides =
    fleetConfig?.scoring_weight_overrides &&
    Object.keys(fleetConfig.scoring_weight_overrides).length > 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sliders size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">Scoring Weights</h2>
          {usingOverrides ? (
            <span className="text-[9px] bg-brand-teal/10 text-brand-teal-dark px-1.5 py-0.5 rounded border border-brand-teal/20">
              overridden
            </span>
          ) : (
            <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              defaults
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={fleetConfigLoading || saving || (!usingOverrides && !dirty)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40"
            title="Reset to 0.25/0.20/0.20/0.20/0.15 defaults"
          >
            <RotateCcw size={11} />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving || sumOffBy > 0.6}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors',
              dirty
                ? 'bg-brand-teal text-navy-900 hover:bg-brand-teal/90'
                : 'bg-slate-100 text-slate-400',
            )}
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            Save Weights
          </button>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-2.5">
        {COMPONENT_ORDER.map((key) => {
          const rawPct = (local[key] * 100).toFixed(1);
          const normPct = (normalized[key] * 100).toFixed(1);
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-0.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <span>{key}</span>
                <span>
                  <span className="text-navy-900">{rawPct}%</span>
                  <span className="ml-1 text-slate-400 font-normal normal-case tracking-normal">
                    ↦ normalized {normPct}%
                  </span>
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={local[key]}
                onChange={(e) => handleChange(key, Number(e.target.value))}
                className={clsx('w-full h-1.5', COMPONENT_COLORS[key])}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-2">
        <span>
          Raw sum: <span className="font-mono text-navy-900">{total.toFixed(2)}</span>
          <span className="ml-1 text-slate-400">(auto-normalized on save)</span>
        </span>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
          <History size={11} />
          Last 10 Updates
        </div>
        {weightHistory.length === 0 ? (
          <div className="text-xs text-slate-400 italic">
            No weight update history recorded yet.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-auto">
            {weightHistory.map((h) => (
              <div
                key={h.id}
                className="text-[11px] border border-slate-200 rounded-lg px-2.5 py-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">
                    {h.trigger === 'manual'
                      ? 'Manual override'
                      : h.trigger === 'auto_tune'
                      ? 'Auto-tuned'
                      : h.trigger || '—'}
                  </span>
                  <span className="text-slate-400">
                    {timeAgo(h.at ?? h.timestamp)}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-1 mt-1 text-[10px]">
                  {COMPONENT_ORDER.map((k) => {
                    const before = h.weights_before?.[k];
                    const after = h.weights_after?.[k];
                    if (before == null && after == null) return null;
                    const changed =
                      before != null && after != null && Math.abs(before - after) > 0.005;
                    return (
                      <div key={k} className="text-center">
                        <div className="text-slate-400 uppercase tracking-wider">
                          {k.slice(0, 4)}
                        </div>
                        <div
                          className={clsx(
                            'font-mono',
                            changed ? 'text-brand-teal-dark font-semibold' : 'text-slate-600',
                          )}
                        >
                          {before != null ? (before * 100).toFixed(0) : '—'}→
                          {after != null ? (after * 100).toFixed(0) : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {h.explanation && (
                  <div className="text-slate-500 italic mt-1">{h.explanation}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
